import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { SubscribingComponent } from '@infrastructure/subscribing-component';
import { FavoriteCollectionModalComponent } from '@modals/favorite-collection-modal/favorite-collection-modal.component';
import { ModelModel } from '@models/api-models/model';
import { ComponentVariantModel } from '@models/component-variant';
import { ConfigurationModel } from '@models/configuration';
import { InspirationGroupModel } from '@models/inspiration-group';
import { ModelSizeModel } from '@models/model-size';
import { TempleInclinationEnum } from '@models/variant-temple-inclination';
import { EnduserDisplayService } from '@services/enduserdisplay.service';
import { ManualConfigService } from '@services/manual-config.service';
import { ModalService } from '@services/modal.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AddToFavoriteComponent } from '../add-to-favorite/add-to-favorite';

@Component({
    selector: 'app-favorites-area',
    templateUrl: './favorites-area.component.html',
    styleUrls: ['./favorites-area.component.scss'],
    imports: [AddToFavoriteComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class FavoritesAreaComponent extends SubscribingComponent implements OnInit, OnChanges {

	@Input() model?: ModelModel;
	@Input() configuration: ConfigurationModel | null;
	@Input() modelSize?: ModelSizeModel;
	@Input() variants?: Array<ComponentVariantModel>;
	@Input() variantString: string;
	@Input() isInCollection: boolean = false;
	@Input() favoriteUuid: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
	@Input() templeInclination?: TempleInclinationEnum | null = null;
	public allManualConfigGroups = new Array<InspirationGroupModel>();

	private modelSubject: BehaviorSubject<ModelModel | undefined>;
	private configurationSubject: BehaviorSubject<ConfigurationModel | null>;
	private modelSizeSubject: BehaviorSubject<ModelSizeModel | undefined>;
	private variantsSubject: BehaviorSubject<Array<ComponentVariantModel> | undefined>;
	private applyFavoriteCollectionChangeModalEmitter: EventEmitter<boolean> = new EventEmitter();
	@Output() public applyFavoriteCollectionChangeEmitter: EventEmitter<boolean> = new EventEmitter();

	constructor(
		private manualConfigService: ManualConfigService,
		private enduserDisplayService: EnduserDisplayService,
		private modalService: ModalService
	) {
		super();
	}

	ngOnInit() {
		this.modelSubject = new BehaviorSubject(this.model);
		this.configurationSubject = new BehaviorSubject(this.configuration);
		this.modelSizeSubject = new BehaviorSubject(this.modelSize);
		this.variantsSubject = new BehaviorSubject(this.variants);

		this.subscriptions.push(
			combineLatest([
				this.enduserDisplayService.enduserDisplayClickedObservable(),
				this.modelSubject.asObservable(),
				this.modelSizeSubject.asObservable(),
				this.configurationSubject.asObservable(),
				this.variantsSubject.asObservable()
			]).pipe(debounceTime(500)).subscribe(async ([enduser, model, modelSize, configuration, variants]) => {
				console.debug("init", model);
				if (model) {
					await this.InitFavorites();
				}
			})
		);

		this.subscriptions.push(this.applyFavoriteCollectionChangeModalEmitter.subscribe(async x => {
			this.applyFavoriteCollectionChangeEmitter.emit(x);
		}));

		this.InitFavorites();
	}

	ngOnChanges(simpleChanges: SimpleChanges): void {
		if (simpleChanges['model'] && !simpleChanges['model'].firstChange) {
			this.modelSubject.next(simpleChanges['model'].currentValue);
		}

		if (simpleChanges['modelSize'] && !simpleChanges['modelSize'].firstChange) {
			this.modelSizeSubject.next(simpleChanges['modelSize'].currentValue);
		}

		if (simpleChanges['configuration'] && !simpleChanges['configuration'].firstChange) {
			this.configurationSubject.next(simpleChanges['configuration'].currentValue);
		}

		if (simpleChanges['variants'] && !simpleChanges['variants'].firstChange) {
			this.variantsSubject.next(simpleChanges['variants'].currentValue);
		}
	}

	private async InitFavorites() {
		this.allManualConfigGroups = await this.manualConfigService.getInspirationGroups();
	}

	public showMyCollections() {
		this.modalService.create(FavoriteCollectionModalComponent, {
			variants: this.variants,
			model: this.model,
			configuration: this.configuration,
			modelSize: this.modelSize,
			allManualConfigGroups: this.allManualConfigGroups,
			templeInclination: this.templeInclination,
			favoriteUuid: this.favoriteUuid.value,
			applyFavoriteCollectionChangeEmitter: this.applyFavoriteCollectionChangeModalEmitter
		});
	}

	public applyFavoriteChange(favoriteUuid: (BehaviorSubject<string | null>) | null) {
		if (favoriteUuid)
			this.favoriteUuid.next(favoriteUuid.getValue());
	}

}
